/*============================================================================
  a11y-enhancements.js
  WCAG 2.2 Level AA remediation - Bonfire Visuals, August 2026

  Runtime accessibility fixes that cannot be made in Liquid alone, either
  because the markup is injected by a third-party app after page load, or
  because the fix depends on cart state.

  Covers:
    1. Cart status announcements + accessible cart count   (SC 4.1.3, 4.1.2)
    2. Restoring pinch-to-zoom stripped by Product Personalizer (SC 1.4.4, 1.4.10)
    3. Accessible names for app-injected iframes           (SC 4.1.2)
    4. Judge.me review widget corrections                  (SC 2.5.3, 4.1.2)

  Each block is independent and fails safe. Nothing here throws if the
  element it targets is absent.
============================================================================*/
(function () {
  'use strict';

  /*--------------------------------------------------------------------------
    1. Cart status announcements and accessible count
    Appendix A item 3. Adding to cart previously produced no announcement,
    and the cart icon exposed no item count to assistive technology.
  --------------------------------------------------------------------------*/
  var liveRegion = null;

  function getLiveRegion() {
    if (liveRegion && document.body.contains(liveRegion)) return liveRegion;
    liveRegion = document.getElementById('a11yCartStatus');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'a11yCartStatus';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }
    return liveRegion;
  }

  function announce(message) {
    if (!message) return;
    var region = getLiveRegion();
    // Clearing first forces screen readers to re-announce an identical string,
    // which matters when the same item is added twice in a row.
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 60);
  }

  function itemsLabel(count) {
    return count === 1 ? '1 item' : count + ' items';
  }

  function updateCartLabel(count) {
    var labels = document.querySelectorAll('[data-cart-a11y-label]');
    if (!labels.length) return;
    var base = 'Cart';
    labels.forEach(function (el) {
      el.textContent = base + ', ' + itemsLabel(count);
    });
  }

  function readCartCount(detail) {
    if (detail && detail.cart && typeof detail.cart.item_count === 'number') {
      return detail.cart.item_count;
    }
    return null;
  }

  document.addEventListener('cart:updated', function (evt) {
    var count = readCartCount(evt.detail);
    if (count === null) return;
    updateCartLabel(count);
    announce('Cart updated. ' + itemsLabel(count) + ' in your cart.');
  });

  document.addEventListener('ajaxProduct:added', function (evt) {
    var name = '';
    try {
      if (evt.detail && evt.detail.product && evt.detail.product.title) {
        name = evt.detail.product.title;
      } else if (evt.detail && evt.detail.item && evt.detail.item.product_title) {
        name = evt.detail.item.product_title;
      }
    } catch (e) { /* fall through to the generic message */ }

    announce(name ? name + ' added to your cart.' : 'Item added to your cart.');

    // The add-to-cart event does not always carry the new totals, so read them back.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCartLabel(cart.item_count); })
      .catch(function () { /* the label simply keeps its server-rendered value */ });
  });

  /*--------------------------------------------------------------------------
    2. Restore pinch-to-zoom (SC 1.4.4 Resize Text, SC 1.4.10 Reflow)
    The Product Personalizer app injects its own viewport meta tag with
    user-scalable=0 and maximum-scale=1.0, which disables zoom on mobile.
    This is a theme-side patch. A future app update can re-introduce the tag,
    which is why the observer stays active rather than running once.
  --------------------------------------------------------------------------*/
  var SAFE_VIEWPORT = 'width=device-width, initial-scale=1.0';

  function repairViewport() {
    var metas = document.querySelectorAll('meta[name="viewport"]');
    if (!metas.length) return;

    metas.forEach(function (meta, index) {
      var content = meta.getAttribute('content') || '';
      var blocksZoom = /user-scalable\s*=\s*(no|0)/i.test(content) ||
                       /maximum-scale\s*=\s*(1(\.0+)?|0?\.\d+)\b/i.test(content);
      if (!blocksZoom) return;

      // A duplicate viewport tag is itself invalid, so drop the extras and
      // repair the first one rather than leaving several in the head.
      if (index > 0) {
        meta.parentNode.removeChild(meta);
      } else {
        meta.setAttribute('content', SAFE_VIEWPORT);
      }
    });
  }

  repairViewport();

  if ('MutationObserver' in window) {
    var headObserver = new MutationObserver(function (mutations) {
      var needsCheck = mutations.some(function (m) {
        return Array.prototype.some.call(m.addedNodes || [], function (node) {
          return node.nodeName === 'META';
        }) || (m.type === 'attributes' && m.target.nodeName === 'META');
      });
      if (needsCheck) repairViewport();
    });
    headObserver.observe(document.head, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['content']
    });
  }

  /*--------------------------------------------------------------------------
    3. Accessible names for iframes (SC 4.1.2)
    Appendix A item 1. Theme-rendered video embeds are titled in Liquid.
    This covers iframes that arrive from blog rich text, page builders and
    apps, where we cannot reach the markup at render time.
  --------------------------------------------------------------------------*/
  function describeIframe(frame) {
    var src = frame.getAttribute('src') || '';
    if (/youtube\.com|youtu\.be/i.test(src))   return 'YouTube video player';
    if (/player\.vimeo\.com|vimeo\.com/i.test(src)) return 'Vimeo video player';
    if (/instagram\.com/i.test(src))           return 'Instagram post';
    if (/facebook\.com/i.test(src))            return 'Facebook content';
    if (/spotify\.com/i.test(src))             return 'Spotify player';
    if (/google\.com\/maps|maps\.google/i.test(src)) return 'Map';
    if (/pinterest\.com/i.test(src))           return 'Pinterest post';
    if (/tiktok\.com/i.test(src))              return 'TikTok video';
    if (/soundcloud\.com/i.test(src))          return 'SoundCloud player';

    // Enumerating providers can never be complete, and an untitled frame is a
    // failure regardless of who it belongs to. Anything else with a real src
    // gets a name from its own hostname, so nothing falls through unnamed.
    try {
      var host = new URL(src, window.location.href).hostname.replace(/^www\./, '');
      if (host && host !== window.location.hostname) return 'Embedded content from ' + host;
    } catch (e) {}
    return null;
  }

  function isTrackingFrame(frame) {
    var src = frame.getAttribute('src') || '';
    var name = frame.getAttribute('name') || '';
    return /doubleclick|googletagmanager|google-analytics|facebook\.com\/tr|web-pixel-sandbox|preview-bar/i.test(src + ' ' + name);
  }

  function hasRealSource(frame) {
    var src = frame.getAttribute('src') || '';
    return src !== '' && src !== 'about:blank';
  }

  function titleIframes(root) {
    var frames = (root || document).querySelectorAll('iframe');

    frames.forEach(function (frame) {
      // Analytics and pixel frames carry no content for a user. Hiding them
      // keeps them out of the tab order and out of screen reader output.
      if (isTrackingFrame(frame)) {
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
        return;
      }

      // A frame with no src yet is usually waiting on cookie consent, which
      // is how the OneTrust banner defers YouTube embeds. It must be hidden
      // only provisionally, because the real video arrives later and has to
      // become reachable again once it does.
      if (!hasRealSource(frame)) {
        if (!frame.hasAttribute('data-a11y-provisional')) {
          frame.setAttribute('data-a11y-provisional', '');
          frame.setAttribute('aria-hidden', 'true');
          frame.setAttribute('tabindex', '-1');
        }
        return;
      }

      // A real source is present. Undo any provisional hiding first, so a
      // consent-gated video does not stay invisible to assistive technology.
      if (frame.hasAttribute('data-a11y-provisional')) {
        frame.removeAttribute('data-a11y-provisional');
        frame.removeAttribute('aria-hidden');
        frame.removeAttribute('tabindex');
      }

      if (frame.getAttribute('title') ||
          frame.getAttribute('aria-label') ||
          frame.getAttribute('aria-labelledby')) return;

      var label = describeIframe(frame);
      if (label) frame.setAttribute('title', label);
    });
  }

  /*--------------------------------------------------------------------------
    4. Judge.me review widget (SC 2.5.3 Label in Name, SC 4.1.2)
    The star rating is built from anchors with no href, which several screen
    readers skip entirely, and the visible rating is rendered as symbols so
    the accessible name never matches the visible label. Both are patched
    here because the widget renders client-side.
  --------------------------------------------------------------------------*/
  function repairJudgeMe(root) {
    var scope = root || document;

    // Star anchors with no href are not real links. Expose them as images
    // with a text alternative so the rating is announced once, sensibly.
    scope.querySelectorAll('.jdgm-star').forEach(function (star) {
      if (!star.getAttribute('href')) {
        star.setAttribute('role', 'presentation');
        star.setAttribute('aria-hidden', 'true');
        star.removeAttribute('tabindex');
      }
    });

    // Give the rating a single readable name matching what is seen.
    //
    // Which element carries that name depends on the context. On a product
    // card the star control has already been flattened, so the badge itself
    // becomes an image with a text alternative. On a product page the stars
    // are a real control that scrolls to the reviews, and a focusable control
    // must never sit inside a role="img", so the control is labelled instead.
    scope.querySelectorAll('.jdgm-prev-badge').forEach(function (badge) {
      var avg = badge.getAttribute('data-average-rating');
      if (!avg) return;
      var num = badge.getAttribute('data-number-of-reviews');
      var label = avg + ' out of 5 stars' + (num ? ', ' + num + ' reviews' : '');

      // A badge Judge.me is not showing needs no role at all. Nothing hidden
      // by CSS reaches assistive technology, so a label on it is inert, and
      // giving it role="img" only opens a window in which a link Judge.me
      // injects a moment later sits inside a role that hides its children.
      // That exact snapshot was caught by the scanner twice, on two different
      // products, both times on a display:none badge. getClientRects() is
      // empty for an element hidden by itself or by any ancestor, and stays
      // populated for a visible fixed-position one, which offsetParent would
      // misreport. If Judge.me later shows the badge, the mutation observer
      // re-runs this pass and labels it then.
      if (badge.getClientRects().length === 0) {
        badge.removeAttribute('role');
        badge.removeAttribute('aria-label');
        return;
      }

      // The narrow selector below finds the control Judge.me normally renders.
      // A wider focusable check guards the role="img" branch as well, because
      // a focusable element inside a role that hides its children is a
      // violation no matter which element it happens to be.
      var control = badge.querySelector('[role="button"], [tabindex="0"], a[href], button') ||
                    badge.querySelector(FOCUSABLE);

      if (control) {
        if (badge.getAttribute('role') === 'img') badge.removeAttribute('role');
        badge.removeAttribute('aria-label');
        control.setAttribute('aria-label', label + ', see all reviews');
      } else {
        badge.setAttribute('role', 'img');
        badge.setAttribute('aria-label', label);
      }
    });

    // Judge.me labels both the review form's navigation and the thank-you
    // screen's navigation "Review form navigation". Two landmarks sharing a
    // name are ambiguous when a screen reader lists them, so the second is
    // renamed to say what it actually is.
    scope.querySelectorAll('.jdgm-write-review-modal__thank-you-nav[aria-label]').forEach(function (el) {
      if (el.getAttribute('aria-label') === 'Review submitted') return;
      el.setAttribute('aria-label', 'Review submitted');
    });

    // aria-label is not permitted on a generic div. Give it a role so the
    // name is valid, rather than stripping a useful label.
    scope.querySelectorAll('div[aria-label]:not([role])').forEach(function (el) {
      if (!/^jm-|jdgm/.test(el.className || '')) return;
      el.setAttribute('role', 'group');
    });
  }

  /*--------------------------------------------------------------------------
    5. Expanded-state accuracy (SC 4.1.2 Name, Role, Value)

    Two controls declared an expanded state that was written once in Liquid
    and never updated, so assistive technology was always told "collapsed",
    including while the menu or the search results were open.
  --------------------------------------------------------------------------*/

  // a) Main navigation dropdowns, built on <details>/<summary>.
  /* The nav dropdowns previously had aria-expanded mirrored onto their
     <summary> from here. That has been removed rather than maintained.

     A <summary> that is the summary for its parent <details> is not allowed a
     role, and aria-expanded needs a supporting role, so setting it produced
     invalid ARIA. <details> and <summary> already expose expanded and
     collapsed state natively, so nothing is lost by leaving them alone, and
     any stale attribute left behind by an earlier version is cleared here so
     the two can never disagree. */
  function bindDetailsSync(root) {
    (root || document).querySelectorAll('details.site-nav__details > summary[aria-expanded], details.site-nav__details > summary[aria-controls]')
      .forEach(function (summary) {
        summary.removeAttribute('aria-expanded');
        summary.removeAttribute('aria-controls');
      });
  }

  // b) Predictive search combobox. aria-expanded must reflect whether the
  //    results listbox is currently showing.
  function bindSearchSync(root) {
    (root || document).querySelectorAll('predictive-search:not([data-a11y-sync])')
      .forEach(function (widget) {
        widget.setAttribute('data-a11y-sync', '');
        // The combobox role lives on the wrapper, not on the input: ARIA in
        // HTML does not permit it on <input type="search">. The input is still
        // needed for the input event, so both are looked up.
        var combobox = widget.querySelector('[role="combobox"]');
        var input = widget.querySelector('input[type="search"]');
        var results = widget.querySelector('#predictive-search');
        if (!combobox || !input) return;

        // The search-and-filter app attaches its own component to this input
        // and writes role="combobox" and aria-expanded straight onto it, from
        // componentDidMount and again from componentDidUpdate. Traced to
        // xcloud-search-filter. That is why removing the attributes from the
        // theme markup is not enough on its own: they come back on every page
        // as soon as the app mounts, which is what the audit was reporting.
        //
        // The attributes are moved rather than deleted. The state the app is
        // expressing is real and worth announcing, it is just being put on an
        // element that cannot legally carry it, so it is relayed to the
        // wrapper, which can.
        var appOwnsState = false;

        var moveInvalidAria = function () {
          if (input.hasAttribute('role')) input.removeAttribute('role');
          if (input.hasAttribute('aria-expanded')) {
            appOwnsState = true;
            combobox.setAttribute('aria-expanded', input.getAttribute('aria-expanded'));
            input.removeAttribute('aria-expanded');
          }
        };

        // The theme's own predictive panel, used only when the app is not
        // driving the field. Deferring to the app when it is present stops the
        // two from fighting over the same attribute.
        var update = function () {
          if (appOwnsState || !results) return;
          var open = widget.classList.contains('is-active') &&
                     results.textContent.trim().length > 0;
          combobox.setAttribute('aria-expanded', open ? 'true' : 'false');
        };

        moveInvalidAria();
        update();

        if ('MutationObserver' in window) {
          // Only ever reads and removes on the input, never writes to it, so
          // this cannot loop against the app's own rendering.
          new MutationObserver(moveInvalidAria)
            .observe(input, { attributes: true, attributeFilter: ['role', 'aria-expanded'] });
          if (results) {
            new MutationObserver(update).observe(results, { childList: true, subtree: true });
          }
          new MutationObserver(update).observe(widget, { attributes: true, attributeFilter: ['class'] });
        }
        input.addEventListener('input', function () { window.setTimeout(update, 250); });
      });
  }

  /*--------------------------------------------------------------------------
    6. Controls nested inside links (SC 4.1.2, 2.5.3)

    The Judge.me rating inside a product card renders a focusable
    role="button" labelled "See all reviews" within the card's own link.
    Nesting a control inside a link is invalid, it adds a tab stop that leads
    nowhere, and its label leaks into the card's accessible name where it does
    not match the visible text. The rating itself stays announced, because the
    surrounding badge already carries a role="img" label built in section 4.
  --------------------------------------------------------------------------*/
  function unnestControlsInLinks(root) {
    var scope = root || document;
    var candidates = scope.querySelectorAll('a [role="button"], a [tabindex="0"]');

    candidates.forEach(function (el) {
      // Real controls are left alone. Only faux controls built from spans and
      // divs are flattened, since those are the invalid ones.
      if (/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
      if (!el.closest('a')) return;

      // Deliberately not guarded by a one-shot marker. Judge.me re-applies
      // these attributes when its widget finishes initialising, which is
      // after our first pass, so the check has to run on actual state.
      if (el.hasAttribute('role')) el.removeAttribute('role');
      if (el.hasAttribute('tabindex')) el.removeAttribute('tabindex');
      if (el.hasAttribute('aria-label')) el.removeAttribute('aria-label');
    });
  }

  /*--------------------------------------------------------------------------
    7. Links whose only content is an unnamed placeholder image (SC 2.4.4)

    Empty collection and onboarding states render a placeholder SVG inside a
    link, which leaves the link with no accessible name at all.
  --------------------------------------------------------------------------*/
  // Slugs that are a run of lowercase words and cannot be split reliably.
  // Taken from the destination pages' own titles, so the names match the site.
  var SLUG_LABELS = {
    'about-us': 'About Us',
    'brittneystrickland': 'Brittney Strickland',
    'courtneyellis': 'Courtney Ellis',
    'dianablinkhorn': 'Diana Blinkhorn',
    'janinenyquist': 'Janine Nyquist',
    'jointhecampaign': 'Join the Campaign',
    'kristinhernandez': 'Kristin Hernandez',
    'lannabritt': 'Lanna Britt',
    'lindsayostrom': 'Lindsay Ostrom',
    'morestories': 'More Stories',
    'motherhoodrewritten': 'Motherhood Rewritten',
    'sarastockinger': 'Sara Stockinger',
    'shannonfabin': 'Shannon Fabin',
    'stephaniedavis': 'Stephanie Davis',
    'themagazine': 'Motherhood Rewritten Magazine'
  };

  function labelFromHref(href) {
    if (!href) return '';
    var path = href.split('?')[0].split('#')[0].replace(/\/+$/, '');
    var slug = path.split('/').pop();
    if (!slug || /^https?:$/.test(slug)) return 'Home';

    if (SLUG_LABELS[slug.toLowerCase()]) return SLUG_LABELS[slug.toLowerCase()];

    // Split CamelCase names such as KinseyThurlow, then tidy separators.
    var words = slug
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[-_]+/g, ' ')
      .trim();

    return words.replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  function nameEmptyLinks(root) {
    var scope = root || document;

    scope.querySelectorAll('a[href]').forEach(function (link) {
      if (link.textContent.trim()) return;
      if (link.getAttribute('aria-label') || link.getAttribute('aria-labelledby')) return;
      if (link.getAttribute('title')) return;
      if (link.getAttribute('aria-hidden') === 'true') return;

      var media = link.querySelector('img, svg, .placeholder-svg');

      if (!media) {
        // A link with no text and no image at all is usually leftover markup
        // from the content editor. If it renders at no size it cannot be seen
        // or clicked, so tabbing to it lands on nothing: take it out of the
        // tree and the tab order together, which is only safe because both
        // happen at once. If it does occupy space it is a real clickable
        // region, so name it from where it points instead.
        var box = link.getBoundingClientRect();
        if (box.width < 2 || box.height < 2) {
          link.setAttribute('aria-hidden', 'true');
          link.setAttribute('tabindex', '-1');
        } else {
          var href = link.getAttribute('href') || '';
          if (href.indexOf('javascript:') !== 0) {
            var fallback = labelFromHref(href);
            if (fallback) link.setAttribute('aria-label', fallback);
          }
        }
        return;
      }

      // An image with real alt text already names the link.
      var img = link.querySelector('img[alt]');
      if (img && img.getAttribute('alt').trim()) return;

      // Prefer a real title alongside the image, which is how product cards
      // and the story grids are laid out.
      var card = link.closest('.grid-product__content, .grid__item, .shogun-component') || link.parentElement;
      var titleEl = card && card.querySelector('.grid-product__title, h1, h2, h3, h4');
      var label = titleEl && titleEl.textContent.trim();

      if (!label) label = labelFromHref(link.getAttribute('href'));
      if (label) link.setAttribute('aria-label', label);
    });
  }

  /*--------------------------------------------------------------------------
    8. Redundant ARIA (advisory)

    These are not WCAG failures, but they are noise in the audit report and
    they can confuse older screen readers, which announce the state twice.
    Most instances come from apps rather than the theme.
  --------------------------------------------------------------------------*/
  function stripRedundantAria(root) {
    var scope = root || document;

    // aria-required duplicates the native required attribute.
    scope.querySelectorAll('[aria-required][required]').forEach(function (el) {
      el.removeAttribute('aria-required');
    });

    // role=navigation duplicates the implicit role of <nav>.
    scope.querySelectorAll('nav[role="navigation"]').forEach(function (el) {
      el.removeAttribute('role');
    });

    // An image carrying a real description cannot also be marked decorative.
    // The two say opposite things, and role wins, so the description is thrown
    // away and the image is announced as nothing. Where someone has written alt
    // text, the alt text is what they meant.
    scope.querySelectorAll('img[role="presentation"], img[role="none"]').forEach(function (img) {
      var alt = img.getAttribute('alt');
      if (alt && alt.trim()) img.removeAttribute('role');
    });
  }

  /*--------------------------------------------------------------------------
    9. Product Personalizer form controls (SC 1.3.1, 3.3.2, 4.1.2)

    Zepto renders each option as a <label class="pplrlabel"> followed by a
    sibling control, but the label carries no "for" and the control carries no
    id, so the two are never associated. A screen reader therefore announces
    the personalisation fields with no name at all, falling back to the
    placeholder, which disappears as soon as the customer starts typing.

    These are the fields a customer must complete to buy a personalised
    product, so an unnamed field here is a direct barrier to purchase.
  --------------------------------------------------------------------------*/
  function slugFromName(control, index) {
    var raw = control.getAttribute('name') || control.className || ('field-' + index);
    return 'pplr-' + raw
      .replace(/properties\[|\]/g, '')
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
  }

  function labelZeptoControls(root) {
    var scope = root || document;

    scope.querySelectorAll('.pplr-wrapper').forEach(function (wrap, index) {
      var label = wrap.querySelector('label.pplrlabel');
      if (!label) return;

      var control = wrap.querySelector('input:not([type="hidden"]), select, textarea');
      if (!control) return;

      // A label that wraps its control is already associated natively.
      // Zepto does this for checkboxes, so those need nothing.
      if (label.contains(control)) return;

      if (!control.id) {
        var id = slugFromName(control, index);
        // Guard against two options resolving to the same id on one page.
        var n = 2;
        var candidate = id;
        while (document.getElementById(candidate)) {
          candidate = id + '-' + n;
          n++;
        }
        control.id = candidate;
      }

      if (!label.getAttribute('for')) label.setAttribute('for', control.id);

      // The price suffix is decorative and reads as a stray empty node.
      var price = label.querySelector('.pplr_option_price_span');
      if (price && !price.textContent.trim()) price.setAttribute('aria-hidden', 'true');

      // Associate the character counter so the limit is announced when the
      // field takes focus. Deliberately not a live region, because Zepto
      // rewrites it on every keystroke and it would talk over the typing.
      var counter = wrap.querySelector('.pplr-character-count');
      if (counter) {
        if (!counter.id) counter.id = control.id + '-limit';
        var described = control.getAttribute('aria-describedby') || '';
        if (described.indexOf(counter.id) === -1) {
          control.setAttribute('aria-describedby', (described + ' ' + counter.id).trim());
        }
      }
    });
  }

  /*--------------------------------------------------------------------------
    10. Selleasy upsell widget (SC 2.1.1, 4.1.1, 4.1.2)

    Two problems, both of which stop a keyboard or screen reader user from
    using the "frequently bought together" upsell at all:

      a) The Add control is a plain <div>. It cannot be reached by Tab and has
         no role, so assistive technology does not report it as a control.
      b) Every variant <select> is output with the same id, "variantsLabel",
         and no label. Duplicate ids are invalid and break any association,
         and the field is announced with no name.
  --------------------------------------------------------------------------*/
  function repairSelleasy(root) {
    var scope = root || document;

    // a) Make the Add control a real button.
    scope.querySelectorAll('.ppa-add-to-cart-btn').forEach(function (btn) {
      if (btn.getAttribute('role') !== 'button') btn.setAttribute('role', 'button');
      if (btn.getAttribute('tabindex') === null) btn.setAttribute('tabindex', '0');

      if (btn.hasAttribute('data-a11y-keys')) return;
      btn.setAttribute('data-a11y-keys', '');
      btn.addEventListener('keydown', function (evt) {
        if (evt.key !== 'Enter' && evt.key !== ' ' && evt.key !== 'Spacebar') return;
        evt.preventDefault();
        btn.click();
      });

      // Name it after the product it adds, so a list of buttons is not just
      // "Add, Add, Add".
      var card = btn.closest('.ppa-prod-container-grid');
      var title = card && card.querySelector('.ppa-prod-details-title-grid');
      var name = title && title.textContent.trim();
      if (name) btn.setAttribute('aria-label', 'Add ' + name + ' to cart');

      // Selleasy adds to the cart through its own request rather than the
      // theme's, so it never fires the events the live region listens for.
      // Read the cart back after the click and announce the result, otherwise
      // a screen reader user gets no confirmation the add worked at all.
      btn.addEventListener('click', function () {
        window.setTimeout(function () {
          fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              updateCartLabel(cart.item_count);
              announce((name ? name + ' added to your cart. ' : 'Item added to your cart. ') +
                       itemsLabel(cart.item_count) + ' in your cart.');
            })
            .catch(function () {});
        }, 900);
      });
    });

    // a2) The carousel arrows are divs containing only a chevron glyph, so
    //     they cannot be tabbed to and a screen reader reads the symbol.
    scope.querySelectorAll('.lb-upsell-ppa-carousel-nav').forEach(function (nav) {
      if (nav.getAttribute('role') !== 'button') nav.setAttribute('role', 'button');
      if (nav.getAttribute('tabindex') === null) nav.setAttribute('tabindex', '0');

      if (!nav.getAttribute('aria-label')) {
        var glyph = nav.textContent.trim();
        var isPrev = glyph.indexOf('\u276e') !== -1 || glyph.indexOf('\u2039') !== -1 || glyph.indexOf('<') === 0;
        nav.setAttribute('aria-label', isPrev ? 'Previous recommendations' : 'Next recommendations');
      }

      if (nav.hasAttribute('data-a11y-keys')) return;
      nav.setAttribute('data-a11y-keys', '');
      nav.addEventListener('keydown', function (evt) {
        if (evt.key !== 'Enter' && evt.key !== ' ' && evt.key !== 'Spacebar') return;
        evt.preventDefault();
        nav.click();
      });
    });

    // b) Give each upsell select a unique id and a real name.
    var seen = 0;
    scope.querySelectorAll('select.lb-upsell-variants-select').forEach(function (sel) {
      seen++;
      if (sel.id === 'variantsLabel' || !sel.id) {
        sel.id = 'lb-upsell-variant-' + seen;
      }

      if (sel.getAttribute('aria-label')) return;

      // The first, disabled option carries the option-set name, e.g. "Symbol".
      var placeholder = sel.querySelector('option[disabled]');
      var optionName = placeholder ? placeholder.textContent.trim() : '';

      var card = sel.closest('.ppa-prod-container-grid');
      var title = card && card.querySelector('.ppa-prod-details-title-grid');
      var product = title ? title.textContent.trim() : '';

      var label = [optionName, product].filter(Boolean).join(' for ');
      sel.setAttribute('aria-label', label || 'Product option');
    });
  }

  /*--------------------------------------------------------------------------
    11. GemPages hero banners (SC 1.3.1, 2.1.1, 4.1.2)

    GemPages wraps the entire contents of a hero banner, heading, body copy and
    call to action alike, in a div carrying aria-hidden="true". The effect is
    that a screen reader user is told nothing at all about the hero, while the
    link inside it stays in the tab order, so keyboard users tab into content
    they are never told exists.

    Handled at runtime rather than by editing the generated gp-*.liquid files,
    because re-publishing from the GemPages editor rewrites those files and
    would silently undo any edit made there.
  --------------------------------------------------------------------------*/
  function hrefFromHeroData(wrapper) {
    var host = wrapper.closest('gp-hero-banner');
    if (!host) return '';
    try {
      var data = JSON.parse(host.getAttribute('gp-data') || '{}');
      return data.href || '';
    } catch (e) {
      return '';
    }
  }

  function repairHeroBanners(root) {
    var scope = root || document;

    scope.querySelectorAll('[data-hero-banner-link]').forEach(function (wrapper) {
      // Never hide the hero's own content from assistive technology.
      if (wrapper.getAttribute('aria-hidden') === 'true') {
        wrapper.removeAttribute('aria-hidden');
      }

      var innerLink = wrapper.querySelector('a[href]');

      if (innerLink) {
        // A real link is present, so it carries the name and the keyboard
        // access. aria-label is not permitted on a generic div anyway.
        wrapper.removeAttribute('aria-label');
        wrapper.removeAttribute('role');
        wrapper.removeAttribute('tabindex');
        return;
      }

      // No inner link, so this wrapper is the only way to activate the banner
      // and has to behave like one.
      var href = hrefFromHeroData(wrapper);
      if (!href) return;

      if (wrapper.getAttribute('role') !== 'link') wrapper.setAttribute('role', 'link');
      if (wrapper.getAttribute('tabindex') === null) wrapper.setAttribute('tabindex', '0');

      // Name it from the visible heading so the name matches what is seen,
      // rather than the generic "Hero banner link" GemPages supplies.
      var heading = wrapper.querySelector('h1, h2, h3, h4, h5, h6');
      var label = heading && heading.textContent.trim();
      if (!label) {
        var slug = href.split('?')[0].replace(/\/$/, '').split('/').pop();
        label = slug ? slug.replace(/[-_]+/g, ' ') : 'Featured banner';
      }
      wrapper.setAttribute('aria-label', label);

      if (wrapper.hasAttribute('data-a11y-keys')) return;
      wrapper.setAttribute('data-a11y-keys', '');
      wrapper.addEventListener('keydown', function (evt) {
        if (evt.key !== 'Enter') return;
        evt.preventDefault();
        wrapper.click();
      });
    });
  }

  /*--------------------------------------------------------------------------
    12. Safety net: focusable content inside aria-hidden (SC 4.1.2, 2.4.3)

    Anything still hidden from assistive technology must also be out of the
    tab order, otherwise keyboard users land on controls that are never
    announced. This catches page-builder and app markup we have not named
    individually.
  --------------------------------------------------------------------------*/
  // iframe is included deliberately. An iframe is a tab stop and everything
  // inside it is reachable, so an iframe inside an aria-hidden subtree strands
  // a whole embedded document, not just one control. Leaving it out is what let
  // an empty <h2> wrapping an Instagram embed be hidden on a blog post.
  var FOCUSABLE = 'a[href], button, input, select, textarea, iframe, [tabindex]:not([tabindex="-1"]), [role="button"], [role="link"]';

  function sealAriaHidden(root) {
    var scope = root || document;

    // Reversible by design. Drawers and modals toggle aria-hidden as they open
    // and close, so anything sealed while hidden must be released the moment
    // it is shown again, or the cart drawer becomes unreachable by keyboard.
    document.querySelectorAll('[data-a11y-sealed]').forEach(function (el) {
      if (el.closest('[aria-hidden="true"]')) return;
      el.removeAttribute('tabindex');
      el.removeAttribute('data-a11y-sealed');
    });

    scope.querySelectorAll('[aria-hidden="true"]').forEach(function (hidden) {
      hidden.querySelectorAll(FOCUSABLE).forEach(function (el) {
        if (el.getAttribute('tabindex') === '-1') return;
        // Only ever remove a tabindex we added ourselves.
        el.setAttribute('data-a11y-sealed', '');
        el.setAttribute('tabindex', '-1');
      });
    });
  }

  /*--------------------------------------------------------------------------
    13. Empty headings (SC 1.3.1, 2.4.6)

    Screen reader users navigate long pages by jumping between headings, so an
    empty heading is a blank entry in that list that leads nowhere. Shogun
    leaves these behind on pages that have been edited over time.

    The element is not removed, because page builders and the theme both use
    headings for spacing. It is hidden from assistive technology instead,
    which takes it out of the heading list while leaving the layout intact.
  --------------------------------------------------------------------------*/
  function hideEmptyHeadings(root) {
    var scope = root || document;

    // Reversible, for the same reason sealAriaHidden is. Embeds arrive late:
    // Instagram and YouTube replace their placeholder well after this first
    // runs, so a heading that was genuinely empty on the first pass can have a
    // whole iframe in it by the third. Anything hidden earlier is released as
    // soon as it has content again, otherwise the fix becomes the failure.
    document.querySelectorAll('[data-a11y-empty-heading]').forEach(function (heading) {
      var hasText = heading.textContent.replace(/\u00a0/g, ' ').trim();
      if (!hasText && !heading.querySelector(FOCUSABLE + ', img, svg, video')) return;
      heading.removeAttribute('aria-hidden');
      heading.removeAttribute('data-a11y-empty-heading');
    });

    scope.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(function (heading) {
      if (heading.getAttribute('aria-hidden') === 'true') return;
      if (heading.textContent.replace(/\u00a0/g, ' ').trim()) return;
      // An image or icon with a text alternative still counts as content, and
      // so does any embedded media, whether or not it carries a label.
      if (heading.querySelector('img[alt]:not([alt=""]), svg title, [aria-label]')) return;
      if (heading.querySelector('iframe, video, img, svg')) return;
      // A heading with no text can still wrap a link or control. Hiding it
      // would leave focusable content inside an aria-hidden subtree, which is
      // its own failure and strands the control for screen reader users.
      if (heading.querySelector(FOCUSABLE)) return;

      heading.setAttribute('aria-hidden', 'true');
      heading.setAttribute('data-a11y-empty-heading', '');
    });
  }

  /*--------------------------------------------------------------------------
    Run the DOM-dependent passes now and again whenever apps inject markup.
  --------------------------------------------------------------------------*/
  /*--------------------------------------------------------------------------
    14. Gift Card Pro checkout fields (SC 1.3.1, 3.3.2, 4.1.2)

    The gift card page at /a/gc/gift-card/ renders through the app proxy, so it
    loads inside this theme's layout and this file reaches it. Every field a
    customer has to complete to send a gift card is unnamed:

      - Email, Name and From each have a real visible <label> sitting right
        before the input, but the label carries no "for" and the input carries
        no id, so the two were never associated. A screen reader announces
        "edit, blank" and falls back to the placeholder, which disappears the
        moment someone starts typing.
      - The phone field has no visible label at all. It sits inside the
        intl-tel-input wrapper, whose text content is the entire country list,
        so the surrounding text cannot be borrowed and a written name is needed.
      - The greeting card images are radio buttons whose <img> alt is empty and
        whose label has no text, so all four announce identically as
        "radio button, not checked".

    This is the checkout path for a gift card, so an unnamed field here is a
    direct barrier to buying one.
  --------------------------------------------------------------------------*/
  function labelGiftCardFields(root) {
    var scope = root || document;

    // a) Inputs that already have a visible label, just an unassociated one.
    scope.querySelectorAll('.gc__input_container').forEach(function (group, index) {
      var label = group.querySelector('label.gc__input_label');
      var control = group.querySelector('input:not([type="hidden"]), select, textarea');
      if (!label || !control) return;
      if (label.contains(control)) return;
      if (!label.textContent.trim()) return;
      if (control.getAttribute('aria-label') || control.getAttribute('aria-labelledby')) return;

      if (!control.id) {
        var base = 'a11y-gc-' + (control.getAttribute('name') || 'field')
          .replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
        var candidate = base;
        var n = 2;
        while (document.getElementById(candidate)) { candidate = base + '-' + n; n++; }
        control.id = candidate;
      }
      if (label.getAttribute('for') !== control.id) label.setAttribute('for', control.id);
    });

    // b) The phone field, which has no visible label to borrow. Named from the
    //    property it posts so the wording matches the rest of the form.
    scope.querySelectorAll('input[type="tel"]').forEach(function (tel) {
      if (!tel.closest('.gc__form, .gc__input_container, .intl-tel-input')) return;
      // A label that exists but is empty is not a name, so the text is what
      // gets checked rather than the presence of the element.
      if (tel.getAttribute('aria-label') || tel.getAttribute('aria-labelledby')) return;
      if (tel.labels && [].some.call(tel.labels, function (l) { return l.textContent.trim(); })) return;
      var name = tel.getAttribute('name') || '';
      tel.setAttribute('aria-label',
        name.indexOf('recipient') > -1 ? 'Recipient phone number' : 'Phone number');
    });

    // c) The greeting card images. The alt text is empty and the value is an
    //    S3 URL, so there is nothing real to name them from. A numbered name is
    //    honest and, more to the point, it makes the four distinguishable,
    //    which is the whole problem. The group gets the instruction that is
    //    already printed above it.
    var cards = scope.querySelectorAll('.gc__image_radio_input');
    cards.forEach(function (radio, index) {
      var label = radio.closest('label');
      if (label && label.textContent.trim()) return;
      if (radio.getAttribute('aria-label')) return;

      var img = label && label.querySelector('img');
      var alt = img && (img.getAttribute('alt') || '').trim();
      var text = alt || 'Greeting card design ' + (index + 1);

      // The text goes inside the wrapping <label> rather than onto the input as
      // an aria-label. Both would name the control, but the label element is
      // empty as it stands, and an empty label is its own failure: it is
      // announced as a labelled control with no label. Putting real text in it
      // fixes the control and the label together, natively, with no ARIA.
      if (label) {
        var sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = text;
        label.appendChild(sr);
      } else {
        radio.setAttribute('aria-label', text);
      }
    });

    // d) The step breadcrumbs. These are <a> elements with no href, carrying
    //    role="button" and tabindex="-1", which puts them out of the tab order
    //    entirely. They are not decorative: clicking "Gift Card" was verified
    //    to switch the active step, change the visible panel and move the URL
    //    to /digital/purchase. So a mouse can move through the gift card flow
    //    and a keyboard cannot, which is a straight 2.1.1 failure on a path
    //    someone has to complete in order to buy something.
    //
    //    The step that is genuinely unavailable keeps its -1 and is marked
    //    disabled rather than being made focusable, so the tab order still
    //    reflects what can actually be used.
    scope.querySelectorAll('a.gc__breadcrumb_button').forEach(function (step) {
      var disabled = step.classList.contains('gc__breadcrumb_disabled');

      if (disabled) {
        step.setAttribute('aria-disabled', 'true');
        step.setAttribute('tabindex', '-1');
        return;
      }

      step.setAttribute('tabindex', '0');
      if (step.classList.contains('gc__breadcrumb_active')) {
        step.setAttribute('aria-current', 'step');
      } else {
        step.removeAttribute('aria-current');
      }

      // runPasses re-runs on every mutation, so the handler is bound once.
      if (step.hasAttribute('data-a11y-key')) return;
      step.setAttribute('data-a11y-key', '');
      step.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        e.preventDefault();
        step.click();
      });
    });

    if (cards.length) {
      var group = cards[0].closest('.gc__form_group, .gc__image_radio_group, fieldset') ||
                  cards[0].parentElement.parentElement;
      if (group && !group.getAttribute('role')) {
        var help = document.querySelector('.gc__form_help');
        group.setAttribute('role', 'radiogroup');
        group.setAttribute('aria-label',
          (help && help.textContent.trim()) || 'Select a greeting card image');
      }
    }
  }

  function runPasses(root) {
    titleIframes(root);
    unnestControlsInLinks(root);
    repairJudgeMe(root);
    bindDetailsSync(root);
    bindSearchSync(root);
    nameEmptyLinks(root);
    labelZeptoControls(root);
    labelGiftCardFields(root);
    repairSelleasy(root);
    repairHeroBanners(root);
    hideEmptyHeadings(root);
    stripRedundantAria(root);
    sealAriaHidden(root);
  }

  function init() {
    getLiveRegion();
    runPasses(document);

    if (!('MutationObserver' in window)) return;

    // Third-party widgets finish rendering well after DOMContentLoaded, so
    // re-run on a short schedule as well as on mutations.
    [400, 1200, 3000, 6000].forEach(function (delay) {
      window.setTimeout(function () { runPasses(document); }, delay);
    });

    var pending = null;
    var bodyObserver = new MutationObserver(function () {
      // Apps insert markup in bursts, so coalesce into one pass per frame.
      if (pending) return;
      pending = window.requestAnimationFrame(function () {
        pending = null;
        runPasses(document);
      });
    });
    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'aria-hidden']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
